/**
 * "Analyse adjustments" for an open basket (owner answer 5, option A): ranks every candidate
 * adjustment on the LIVE public Delta India chain with the simulator's adjustment engine.
 * The server runs it in a forked worker process; this drawer only queues it and polls.
 * Read-only: nothing here, or on the server path behind it, can place an order.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, Drawer, IconButton, LinearProgress, Stack, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAppTheme } from '../../hooks/useAppTheme';
import { optionsimService, simError, SimAltRow, SimAnalyseSummary, SimRunRow } from '../../services/optionsim.service';
import { AltTable, Stat, StatGrid, useIsPhone } from './simUi';
import { dateTimeUtc, inrText, legLabel, money, num, pct, signedMoney } from './simFormat';

const SYMBOL_OK = /^(?:[CP]-BTC-\d+(?:\.\d+)?-\d{6}|BTCUSD)$/;

/** Only Delta BTC options (and the BTCUSD perp) can be analysed. */
export const isAnalysable = (legs: Array<{ symbol?: string }> | undefined): boolean =>
  !!legs?.length && legs.every(l => SYMBOL_OK.test(String(l.symbol || '').toUpperCase()));

const POLL_MS = 1500;

/**
 * The recommendation in words, from the SAME rows the comparison shows (the engine's own sentence
 * rounds to whole dollars first, so its ₹ figures would drift from the table's by a few rupees).
 */
function reasonText(s: SimAnalyseSummary, fx: number): string {
  const { chosen: c, hold: h, closeAll: x } = s;
  const loss = (v: number) => money(Math.max(0, -v), fx);
  if (/^best risk reduction/.test(s.reason) && c.id !== h.id && c.id !== x.id) {
    return `Best risk reduction per ₹ given up (score ${num(c.score, 2)}): the tail loss (ES99) falls from ${loss(h.es99Usd)} to ${loss(c.es99Usd)} and the stress loss from ${loss(h.stressUsd)} to ${loss(c.stressUsd)}, for ${money(c.giveUp, fx)} given up, ${money(c.costUsd, fx)} of it trading cost. It beats closing on expected P&L after costs (${signedMoney(c.evUsd, fx)} against ${signedMoney(x.evUsd, fx)}).`;
  }
  return inrText(s.reason, fx);
}
const GIVE_UP_MS = 4 * 60_000;

const Compare: React.FC<{ hold: SimAltRow; chosen: SimAltRow; closeAll: SimAltRow; fx: number }> = ({ hold, chosen, closeAll, fx }) => {
  const { C } = useAppTheme();
  const phone = useIsPhone();
  const cols = [
    { k: 'Hold', r: hold, best: false },
    ...(chosen.id !== hold.id && chosen.id !== closeAll.id ? [{ k: phone ? 'Best' : 'Recommended', r: chosen, best: true }] : []),
    { k: 'Close all', r: closeAll, best: false },
  ];
  const tone = (v: number) => (v > 0 ? C.bull : v < 0 ? C.bear : undefined);
  const rows: Array<[string, (r: SimAltRow) => string, ((r: SimAltRow) => string | undefined)?, string?]> = [
    ['Cost now', r => money(r.costUsd, fx), undefined, 'Fees + modelled slippage of doing it now.'],
    ['Expected P&L', r => signedMoney(r.evUsd, fx), r => tone(r.evUsd), 'Mean scenario P&L over the next 24 h, after costs.'],
    ['Tail (ES99)', r => signedMoney(r.es99Usd, fx), r => tone(r.es99Usd), 'Average of the worst 1% of scenarios.'],
    ['Stress', r => signedMoney(r.stressUsd, fx), r => tone(r.stressUsd), 'Worst point of the stress grid (±30% spot, IV shocks).'],
    ['Margin', r => (r.utilPct == null ? money(r.imUsd, fx) : phone ? pct(r.utilPct, 1) : `${money(r.imUsd, fx)} · ${pct(r.utilPct, 1)}`), undefined, 'Initial margin after the action, and its share of capital.'],
    ['Net Δ', r => `${num(r.delta, 3)} BTC`, undefined, 'Net delta after the action.'],
    ['θ per day', r => signedMoney(r.theta, fx), r => tone(r.theta), 'Time decay per day after the action.'],
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `minmax(84px, auto) repeat(${cols.length}, minmax(0, 1fr))`, columnGap: 1, rowGap: 0.5, alignItems: 'baseline' }} data-testid="analyse-compare">
      <Box />
      {cols.map(c => <Typography key={c.k} variant="caption" fontWeight={800} color={c.best ? 'primary' : 'text.secondary'} noWrap>{c.k}</Typography>)}
      {rows.map(([label, f, col, hint]) => (
        <React.Fragment key={label}>
          <Tooltip title={hint || ''} arrow placement="left"><Typography variant="caption" color="text.secondary" noWrap>{label}</Typography></Tooltip>
          {cols.map(c => (
            <Typography key={c.k} variant="caption" fontWeight={c.best ? 800 : 600} noWrap sx={{ color: col?.(c.r), fontVariantNumeric: 'tabular-nums' }}>{f(c.r)}</Typography>
          ))}
        </React.Fragment>
      ))}
    </Box>
  );
};

export const AnalyseDrawer: React.FC<{ open: boolean; onClose: () => void; basketId: number; title: string }> = ({ open, onClose, basketId, title }) => {
  const { C } = useAppTheme();
  const phone = useIsPhone();
  const [run, setRun] = useState<SimRunRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** From the POST until the run is terminal (or the poll gives up). */
  const [busy, setBusy] = useState(false);
  const [capital, setCapital] = useState(250000);
  const [margin, setMargin] = useState<'CROSS' | 'PM'>('CROSS');
  const [showAll, setShowAll] = useState(false);
  const pollRef = useRef<{ timer: any; runId: number } | null>(null);
  const lastRunId = useRef<number | null>(null);

  const stopPoll = () => { if (pollRef.current) clearTimeout(pollRef.current.timer); pollRef.current = null; };

  const poll = useCallback((runId: number) => {
    const t0 = Date.now();
    const tick = async () => {
      if (pollRef.current?.runId !== runId) return;
      try {
        const r = await optionsimService.getRun(runId);
        if (pollRef.current?.runId !== runId) return;
        setRun(r);
        if (r.status === 'DONE' || r.status === 'FAILED' || r.status === 'CANCELLED') { stopPoll(); setBusy(false); return; }
      } catch { /* keep polling */ }
      if (Date.now() - t0 > GIVE_UP_MS) {
        stopPoll(); setBusy(false);
        setError('No result after 4 minutes. It may still finish on the server: press Re-run later.');
        return;
      }
      if (pollRef.current?.runId === runId) pollRef.current.timer = setTimeout(tick, POLL_MS);
    };
    stopPoll();
    setBusy(true);
    lastRunId.current = runId;
    pollRef.current = { timer: setTimeout(tick, 400), runId };
  }, []);

  const start = useCallback(async () => {
    stopPoll();
    setError(null); setRun(null); setBusy(true); setShowAll(false);
    try {
      const r = await optionsimService.analyseBasket(basketId, { capitalInr: capital, marginMode: margin });
      poll(r.id);
    } catch (e: any) {
      setError(simError(e)); setBusy(false);
    }
  }, [basketId, capital, margin, poll]);

  // The first open runs it; a re-open shows the last result (with its as-of time) and resumes
  // polling if it was still running. Closing only stops the polling: the server finishes the run.
  const started = useRef(false);
  useEffect(() => {
    if (!open) { stopPoll(); return; }
    if (!started.current) { started.current = true; void start(); return; }
    if (busy && lastRunId.current != null && !pollRef.current) poll(lastRunId.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => () => stopPoll(), []);

  const s = (run?.status === 'DONE' ? run.summary : null) as SimAnalyseSummary | null;
  const fx = s?.usdInr ?? 85;
  const running = busy;
  const g = s?.greeksBefore;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      slotProps={{ paper: { sx: { width: phone ? '100vw' : 'min(760px, 92vw)', maxWidth: '100vw' } } }}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, overflowX: 'hidden' }} data-testid="analyse-drawer">
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
          <InsightsIcon color="primary" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>Analyse adjustments · {title}</Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              Ranks every adjustment on the live Delta India chain with the simulator's engine. Analysis only: no order is placed.
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close"><CloseIcon fontSize="small" /></IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <TextField size="small" type="number" label="Capital (₹)" value={capital} sx={{ width: 140 }}
            onChange={e => setCapital(Math.max(10000, Number(e.target.value) || 0))} inputProps={{ step: 10000 }} />
          <ToggleButtonGroup size="small" exclusive value={margin} onChange={(_, v) => v && setMargin(v)}>
            <Tooltip title="Your account's mode: Delta's tiered cross formula (labelled [A] for options)." arrow><ToggleButton value="CROSS">Cross</ToggleButton></Tooltip>
            <Tooltip title="Independent PM-shaped stress margin [A]; not Delta's number." arrow><ToggleButton value="PM">PM [A]</ToggleButton></Tooltip>
          </ToggleButtonGroup>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} disabled={running} onClick={() => { void start(); }} data-testid="analyse-rerun">
            {run || error ? 'Re-run' : 'Run'}
          </Button>
        </Stack>

        {running && (
          <Box sx={{ my: 2 }} data-testid="analyse-running">
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {run?.status === 'QUEUED' ? 'Queued behind another simulation…' : run?.phase ? `Running: ${run.phase}…` : 'Starting…'} This usually takes 5–20 seconds.
            </Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ my: 1.5 }} data-testid="analyse-error">{error}</Alert>}
        {run?.status === 'FAILED' && <Alert severity="error" sx={{ my: 1.5 }} data-testid="analyse-error">Analysis failed: {run.error}</Alert>}
        {run?.status === 'CANCELLED' && <Alert severity="warning" sx={{ my: 1.5 }}>The analysis was cancelled.</Alert>}

        {s && (
          <Stack spacing={2} data-testid="analyse-result">
            <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'primary.main', bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {s.chosen.id === s.hold.id ? 'Recommendation: hold' : s.chosen.id === s.closeAll.id ? 'Recommendation: close' : 'Recommended adjustment'}
                </Typography>
                {s.chosen.breaches.length > 0 && <Chip size="small" color="warning" label={`limit: ${s.chosen.breaches.join(', ')}`} sx={{ height: 18 }} />}
              </Stack>
              <Typography variant="subtitle1" fontWeight={800} sx={{ overflowWrap: 'anywhere' }} data-testid="analyse-chosen">{s.chosen.letter} · {s.chosen.label}</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }} data-testid="analyse-reason">{reasonText(s, fx)}</Typography>
              <Divider sx={{ my: 1.25 }} />
              <Compare hold={s.hold} chosen={s.chosen} closeAll={s.closeAll} fx={fx} />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Position now</Typography>
              <StatGrid min={120}>
                <Stat label="Spot (index)" value={`$${num(s.spot, 0)}`} hint={`Delta .DEXBTUSD, as of ${dateTimeUtc(s.asOf)} UTC`} />
                <Stat label="Open P&L" value={s.pnlKnown ? signedMoney(s.pnlUsd, fx) : '—'} tone={s.pnlKnown ? (s.pnlUsd >= 0 ? 'bull' : 'bear') : null} hint={s.pnlKnown ? 'At mark, before exit costs, from the recorded fill prices.' : 'Some legs have no recorded fill price.'} />
                <Stat label="Net Δ" value={`${num(g?.delta, 3)} BTC`} hint="Net delta of the basket, in BTC." />
                <Stat label="Γ per 1%" value={num(g?.gammaPer1Pct, 3)} hint="Change of net delta (BTC) for a 1% index move." />
                <Stat label="Vega" value={signedMoney(g?.vega, fx)} hint="P&L for +1 vol point." />
                <Stat label="θ per day" value={signedMoney(g?.theta, fx)} tone={g && g.theta >= 0 ? 'bull' : 'bear'} hint="Time decay per day." />
                <Stat label="Cross margin" value={money(s.margin.crossImUsd, fx)} hint={s.margin.crossLabel} />
                <Stat label="PM margin [A]" value={money(s.margin.pmImUsd, fx)} hint={s.margin.pmLabel} />
              </StatGrid>
              <Stack spacing={0.5} sx={{ mt: 1 }} data-testid="analyse-legs">
                {s.legs.map(l => (
                  <Stack key={l.symbol} direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color: l.qty < 0 ? C.bear : C.bull, minWidth: 64 }}>{l.qty < 0 ? 'SHORT' : 'LONG'} {num(Math.abs(l.qty), 0)}</Typography>
                    <Typography variant="caption" fontWeight={700} noWrap sx={{ minWidth: 0 }}>{legLabel(l.symbol)}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ ml: 'auto !important', fontVariantNumeric: 'tabular-nums' }}>
                      mark ${num(l.mark, 1)}{l.iv != null ? ` · IV ${num(l.iv * 100, 1)}%` : ''}{l.entryPx != null ? ` · entry $${num(l.entryPx, 1)}` : ''}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={800}>All candidates ({num(s.candidates, 0)})</Typography>
                {s.alternatives.length > 10 && <Button size="small" onClick={() => setShowAll(v => !v)}>{showAll ? 'Show top 10' : `Show all ${s.alternatives.length}`}</Button>}
              </Stack>
              <AltTable rows={s.alternatives} fx={fx} chosenId={s.chosen.id} highlight={[s.hold.id, s.closeAll.id]} max={showAll ? 40 : 10} testId="analyse-alternatives" />
            </Box>

            <Typography variant="caption" color="text.secondary" component="div" data-testid="analyse-method">
              Scenarios: {s.scenarioMeta.method === 'fhs' ? 'filtered historical simulation' : 'parametric'} over {num(s.scenarioMeta.horizonSec / 3600, 0)} h
              ({num(s.scenarioMeta.draws, 0)} draws from {num(s.scenarioMeta.windows, 0)} windows of {s.indexHistory}; σ now {s.scenarioMeta.sigmaNowAnnual != null ? pct(s.scenarioMeta.sigmaNowAnnual * 100, 0) : '—'} a year).
              Fills are modelled from marks (no historical bid/ask exists), so costs are estimates. As of {dateTimeUtc(s.asOf)} UTC, ₹ at {num(fx, 2)} per $.
              To act on it, use Legs &amp; adjust: this drawer never places an order.
            </Typography>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

/** The button beside "Legs & adjust": opens the drawer for this basket (BTC options only). */
export const AnalyseButton: React.FC<{ basketId: number; title: string; legs?: Array<{ symbol?: string }>; size?: 'small' | 'medium'; dense?: boolean }> = ({ basketId, title, legs, size = 'small', dense }) => {
  const [open, setOpen] = useState(false);
  // mounted from the first open on, so a re-open keeps the last result and the close animates
  const [mounted, setMounted] = useState(false);
  if (!isAnalysable(legs)) return null;
  return (
    <>
      <Tooltip title="Rank adjustments for this basket on the live chain (analysis only, no order)" arrow>
        <Button size={size} variant="outlined" startIcon={dense ? undefined : <InsightsIcon fontSize="small" />} data-testid={`analyse-open-${basketId}`}
          sx={dense ? { minWidth: 0, py: 0, px: 1, fontSize: '0.6rem' } : undefined}
          onClick={e => { e.stopPropagation(); setMounted(true); setOpen(true); }}>
          Analyse
        </Button>
      </Tooltip>
      {mounted && <AnalyseDrawer open={open} onClose={() => setOpen(false)} basketId={basketId} title={title} />}
    </>
  );
};

export default AnalyseDrawer;
